import { createContext, useContext } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_INITIAL_CALL_INFO } from "../Queries/GetInitialCallInfo";
import { IncidentsContext } from "./IncidentsContext";
import { UserContext } from "./UserContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { PISS_FOR_INCIDENT } from "../Queries/PissForIncident";
import { GET_VEHICLE_INCIDENT_SPECIFICS } from "../Queries/GetVehicleIncidentSpecifics";
import { GET_INCIDENT_DETAIL } from "../Queries/GetIncidentDetail";
import { GET_ARRESTS } from "../Queries/GetArrests";
import { GET_TICKETS } from "../Queries/GetTickets";
import { GET_INCIDENT_REVIEW } from "../Queries/GetIncidentReview";
import { GET_NARRATIVE_SEGMENTS } from "../Components/NarrativeSegments/graphql/GetNarrativeSegments";

export const IncidentSubscriptionContext = createContext();

const IncidentSubscriptionContextProvider = props => {
  const { incidentDispatch } = useContext(IncidentsContext);
  const { user, responseErrorHandler } = useContext(UserContext);
  const {
    SET_INITIAL_CALL_INFO,
    EXTEND_PIS,
    UPDATE_INCIDENT,
    UPDATE_INCIDENT_DETAIL,
    SET_DISPATCH_NARRATIVES,
  } = INCIDENT_ACTIONS;

  const [initialCallInfoSub] = useLazyQuery(GET_INITIAL_CALL_INFO, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        incidentDispatch({ type: SET_INITIAL_CALL_INFO, payload: data });
      }
    },
  });

  const [dispatchNarrativeSub] = useLazyQuery(GET_NARRATIVE_SEGMENTS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      responseErrorHandler(data);
      const incidentId = data?.initialCallInfo?.id;
      const arr = data?.initialCallInfo?.narrativeSegments || [];
      const reversedSegments = arr.slice().reverse();
      incidentDispatch({
        type: SET_DISPATCH_NARRATIVES,
        payload: {
          incidentId: incidentId,
          segments: reversedSegments,
        },
      });
    },
    onError: error => {
      console.error("Error fetching narrative segments:", error);
    },
  });

  const [personIncidentSpecificSub] = useLazyQuery(PISS_FOR_INCIDENT, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        const incidentId = res.id;
        const incidentUuid = res.uuid;
        //pis will be an array so map over it and send each pis to the dispatch to update it
        const piss = res.personIncidentSpecifics;
        piss.forEach(pis => {
          incidentDispatch({
            type: EXTEND_PIS,
            payload: {
              incidentId: incidentId,
              incidentUuid: incidentUuid,
              pisId: pis.id,
              pisUuid: pis.uuid,
              pis: pis,
            },
          });
        });
      }
    },
  });

  const [vehicleDetailSub] = useLazyQuery(GET_VEHICLE_INCIDENT_SPECIFICS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        const incidentId = res[0]?.incident?.id;
        const incidentUuid = res[0]?.incident?.uuid;
        res &&
          res?.length > 0 &&
          incidentDispatch({
            type: UPDATE_INCIDENT,
            payload: {
              id: incidentId,
              uuid: incidentUuid,
              property: "vehicleIncidentSpecifics",
              value: res,
            },
          });
      }
    },
  });

  const [incidentDetailSub] = useLazyQuery(GET_INCIDENT_DETAIL, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        const incidentId = res?.id;
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: { incidentId: incidentId, value: res.incidentDetail },
        });
      }
    },
  });

  const [arrestDetailSub] = useLazyQuery(GET_ARRESTS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        const incidentId = res?.incident?.id;
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: { id: incidentId, property: "incidentArrests", value: res },
        });
      }
    },
  });

  const arrestAndIncidentDetailSub = incidentId => {
    arrestDetailSub({ variables: { incidentId: incidentId } });
    incidentDetailSub({ variables: { incidentId: incidentId } });
  };

  const [ticketFrameSub] = useLazyQuery(GET_TICKETS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const tickets = JSON.parse(JSON.stringify(data[Object.keys(data)[0]]));
        const incidentId = tickets[0]?.incident?.id;
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incidentId,
            property: "incidentTickets",
            value: tickets,
          },
        });
      }
    },
  });

  const [incidentReviewSub] = useLazyQuery(GET_INCIDENT_REVIEW, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        const incidentId = res?.incident?.id;
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: { id: incidentId, property: "incidentReview", value: res },
        });
      }
    },
  });
  return (
    <IncidentSubscriptionContext.Provider
      value={{
        initialCallInfoSub,
        dispatchNarrativeSub,
        personIncidentSpecificSub,
        vehicleDetailSub,
        incidentDetailSub,
        arrestDetailSub,
        arrestAndIncidentDetailSub,
        ticketFrameSub,
        incidentReviewSub,
      }}
    >
      {props.children}
    </IncidentSubscriptionContext.Provider>
  );
};

export default IncidentSubscriptionContextProvider;
